import type { Cover, DeckLayout, TemplateId } from '../types/deck';

/**
 * 템플릿 작성 편집본 md (2026-10-28) — 고친 장 · 칸 글을 사람이 읽을 수 있는 md 로 저장하고, 다시 넣으면 AI 없이 그대로 연다.
 *
 * ```
 * ---
 * grk-template: 1
 * title: 사업 계획
 * guide: v3.3
 * cover: G
 * ---
 *
 * ## 1. 기본 슬라이드 WHITE
 * > 출처: 설계보고.pdf · 3쪽
 *
 * ### [msg] 메시지
 * 사업 목표는 …
 *
 * ### [pic1] 그림
 * ![](현장사진.png)
 * ```
 *
 * 그림은 md 안에 넣지 않는다 — 파일 이름만 적고, 다시 열 때 같은 그림 파일을 함께 넣는다.
 */

export interface EditSlide {
  layout: string;
  /** 칸키 → 글 */
  texts: Record<string, string>;
  /** 그림 칸키 → 올린 그림 파일 이름 */
  pics: Record<string, string>;
  source: string;
  /** AI 가 원문에서 못 찾은 숫자 — 글을 고치면 남은 것만 보인다 */
  unknownNumbers: string[];
}

export interface EditDoc {
  /** 없으면 GRK(2026-10-29 전 편집본) */
  template: TemplateId;
  title: string;
  guide: string;
  cover: Cover;
  slides: EditSlide[];
}

const MARK = 'grk-template: 1';

/** 편집본 md 인가 — 앞머리에 표시가 있으면 */
export const isTemplateMd = (text: string) => text.replace(/^﻿/, '').startsWith(`---\n${MARK}\n`);

export function toTemplateMd(doc: EditDoc, layouts: Map<string, DeckLayout>): string {
  const head = ['---', MARK, `template: ${doc.template}`, `title: ${oneLine(doc.title)}`, `guide: ${doc.guide}`, `cover: ${doc.cover}`, '---', ''];
  const body = doc.slides.map((s, i) => {
    const l = layouts.get(s.layout);
    const out = [`## ${i + 1}. ${s.layout}`];
    if (s.source) out.push(`> 출처: ${oneLine(s.source)}`);
    for (const slot of l?.slots ?? []) {
      if (slot.role === 'pic') {
        if (s.pics[slot.key]) out.push('', `### [${slot.key}] ${slot.label}`, `![](${s.pics[slot.key]})`);
      } else if (s.texts[slot.key]?.trim()) {
        out.push('', `### [${slot.key}] ${slot.label}`, s.texts[slot.key].trim());
      }
    }
    return out.join('\n');
  });
  return `${head.join('\n')}\n${body.join('\n\n')}\n`;
}

export function fromTemplateMd(text: string): EditDoc {
  const src = text.replace(/^﻿/, '').replace(/\r\n/g, '\n');
  if (!isTemplateMd(src)) throw new Error('편집본 md 가 아닙니다(앞머리 grk-template 없음)');
  const end = src.indexOf('\n---\n', 4);
  if (end < 0) throw new Error('편집본 md 앞머리가 닫히지 않았습니다');
  const meta: Record<string, string> = {};
  for (const line of src.slice(4, end).split('\n')) {
    const m = line.match(/^([\w-]+):\s*(.*)$/);
    if (m) meta[m[1]] = m[2].trim();
  }
  const cover = (['G', 'R', 'K'].includes(meta.cover) ? meta.cover : 'G') as Cover;
  const slides: EditSlide[] = [];
  for (const block of src.slice(end + 5).split(/^## (?=\d+\. )/m).slice(1)) {
    const nl = block.indexOf('\n');
    const layout = (nl < 0 ? block : block.slice(0, nl)).replace(/^\d+\.\s*/, '').trim();
    const rest = nl < 0 ? '' : block.slice(nl + 1);
    const source = rest.match(/^> 출처: (.*)$/m)?.[1].trim() ?? '';
    const texts: Record<string, string> = {};
    const pics: Record<string, string> = {};
    for (const part of rest.split(/^### (?=\[)/m).slice(1)) {
      const m = part.match(/^\[([^\]]+)\][^\n]*\n?([\s\S]*)$/);
      if (!m) continue;
      const value = m[2].trim();
      const pic = value.match(/^!\[\]\((.+)\)$/);
      if (pic) pics[m[1]] = pic[1];
      else if (value) texts[m[1]] = value;
    }
    if (layout) slides.push({ layout, texts, pics, source, unknownNumbers: [] });
  }
  if (!slides.length) throw new Error('편집본 md 에 장이 없습니다');
  const template: TemplateId = meta.template === 'RadiARC' ? 'RadiARC' : 'GRK';
  return { template, title: meta.title ?? '', guide: meta.guide ?? '', cover, slides };
}

const oneLine = (s: string) => s.replace(/\s+/g, ' ').trim();
